import { Guard } from "./utils";
import _ from "lodash";
import { DemographicsMotivational } from "./DemographicsMotivational";
import { LandingPage } from "./LandingPage";
import { LoadingPage } from "./features/LoadingPage";
import { PageName, SURVEY_JSON } from "./quiz.config";

const COMPONENT_CONFIG = {
  'experience': {
    nextPage: 'email',
    component: LoadingPage,
    componentName: 'LoadingPage',
  },
  'email': {
    nextPage: 'duration',
    component: DemographicsMotivational,
    componentName: 'Post-Email Page',
  }
} as const;

function isTransitionComponentKey(key: string | null): key is keyof typeof COMPONENT_CONFIG {
  return key !== null && key in COMPONENT_CONFIG;
}

enum QuizSection {
  GETTING_STARTED = 'Getting Started',
  HABITS_AND_BEHAVIORS = 'Habits & Behaviors',
  TRAINING_AND_NUTRITION = 'Training & Nutrition',
};

enum QuizHeading {
  FOUNDATIONS = 'FOUNDATIONS',
  USERS_GOAL = '{name}\'S GOAL',
  HABITS_BEHAVIORS = 'HABITS & BEHAVIORS',
  TRAINING_NUTRITION = 'TRAINING & NUTRITION',
  // web38_hsa_fsa_demand (temporary): chip for the HSA/FSA question that sits
  // after focus in the variation arm. Absent from the model in control.
  FINAL_DETAILS = 'FINAL DETAILS',
};

type HeadingConfig = {
  [key in QuizHeading]: {
    startQuestion: PageName,
    endQuestion: PageName,
  }
};

type SectionConfig = {
  [key in QuizSection]: {
    startQuestion: PageName,
    endQuestion: PageName,
  }
}

type SectionTotal = {
  startIndex: number,
  endIndex: number,
  total: number,
}

type SectionTotals = {
  [key in QuizSection]: SectionTotal;
}

const SECTION_CONFIG: SectionConfig = {
  [QuizSection.GETTING_STARTED]: {
    startQuestion: 'goals',
    endQuestion: 'email',
  },
  [QuizSection.HABITS_AND_BEHAVIORS]: {
    startQuestion: 'duration',
    endQuestion: 'otherPrograms',
  },
  [QuizSection.TRAINING_AND_NUTRITION]: {
    startQuestion: 'workouttrackingapp',
    endQuestion: 'focus',
  },
};

const HEADING_CONFIG: HeadingConfig = {
  [QuizHeading.FOUNDATIONS]: {
    startQuestion: 'goals',
    endQuestion: 'experience',
  },
  [QuizHeading.USERS_GOAL]: {
    startQuestion: 'email',
    endQuestion: 'email',
  },
  [QuizHeading.HABITS_BEHAVIORS]: {
    startQuestion: 'duration',
    endQuestion: 'otherPrograms',
  },
  [QuizHeading.TRAINING_NUTRITION]: {
    startQuestion: 'workouttrackingapp',
    endQuestion: 'focus',
  },
  [QuizHeading.FINAL_DETAILS]: {
    startQuestion: 'hsaFsa' as PageName,
    endQuestion: 'hsaFsa' as PageName,
  },
};

type HeadingConfigTotals = {
  [key in QuizHeading]: SectionTotal;
}

type ValueChoice = {
  value: string,
  text: string,
};

const isValueChoice: Guard<ValueChoice> = (value: any): value is ValueChoice => {
  return typeof value === 'object'
    && 'value' in value && typeof value.value === 'string'
    && 'text' in value && typeof value.text === 'string';
}

type QuizNav = {
  findPageIndex: (name: string) => number;
  getQuestionSection: (questionName: string) => QuizSection | undefined;
  getQuestionHeading: (questionName: string) => QuizHeading | undefined;
  getSectionInfo: (section: QuizSection) => SectionTotal;
};

/**
 * Build the progress/section navigation helpers against a specific ordered page
 * array (the default SURVEY_JSON pages, or a campaign-reordered array). All the
 * index math closes over `surveyPages` instead of the global SURVEY_JSON, so an
 * intro question that was moved to the front is still located correctly.
 *
 * CRITICAL FIX: GETTING_STARTED / FOUNDATIONS anchor their startIndex at 0 (the
 * first page of the effective array) rather than at `findPageIndex('goals')`.
 * With a reordered intro the first question may no longer be `goals`; anchoring
 * at 0 keeps index-0 inside the section so getQuestionSection never returns
 * undefined (which would freeze the progress bar). For the default page order
 * goals is already index 0, so this is a no-op for default traffic.
 */
const makeQuizNav = (surveyPages: typeof SURVEY_JSON.pages): QuizNav => {
  const findPageIndex = (name: string) => {
    let count = 0;

    for (const page of surveyPages) {
      if (page.name === name) return count;
      const component = COMPONENT_CONFIG[page.name as keyof typeof COMPONENT_CONFIG];
      if (component) {
        count++;
        if (component.componentName === name) return count;
      }
      count++;
    }

    return -1;
  };

  const SECTION_QUESTION_COUNT: SectionTotals = _.entries(SECTION_CONFIG).reduce((acc, [section, { startQuestion, endQuestion }]) => {
    const startIndex = section === QuizSection.GETTING_STARTED ? 0 : findPageIndex(startQuestion);
    const endIndex = findPageIndex(endQuestion);
    acc[section as QuizSection] = {
      startIndex,
      endIndex,
      total: endIndex - startIndex + 1,
    }
    return acc;
  }, {} as SectionTotals);

  const SECTION_QUESTION_COUNT_HEADINGS: HeadingConfigTotals = _.entries(HEADING_CONFIG).reduce((acc, [section, { startQuestion, endQuestion }]) => {
    const startIndex = section === QuizHeading.FOUNDATIONS ? 0 : findPageIndex(startQuestion);
    const endIndex = findPageIndex(endQuestion);
    acc[section as QuizHeading] = {
      startIndex,
      endIndex,
      total: endIndex - startIndex + 1,
    }
    return acc;
  }, {} as HeadingConfigTotals);

  const getSectionInfo = (section: QuizSection): SectionTotal => SECTION_QUESTION_COUNT[section];

  // A transform-inserted page can sit between two section ranges. Without a
  // fallback that page resolves to no section and the progress bar silently
  // freezes. Fall back to the nearest section that starts at or before the
  // page; pages inside a range still use the exact match.
  const nearestSectionByStart = <T extends string>(
    questionIndex: number,
    table: Record<T, SectionTotal>
  ): T | undefined => {
    let best: T | undefined;
    let bestStart = -1;
    for (const key of Object.keys(table) as T[]) {
      const { startIndex } = table[key];
      if (startIndex <= questionIndex && startIndex > bestStart) {
        best = key;
        bestStart = startIndex;
      }
    }
    return best;
  };

  const getQuestionSection = (questionName: string): QuizSection | undefined => {
    const questionIndex = findPageIndex(questionName);
    const exact = _.findKey(SECTION_CONFIG, (_value, section) => {
      const { startIndex, endIndex } = SECTION_QUESTION_COUNT[section as QuizSection];
      return questionIndex >= startIndex && questionIndex <= endIndex;
    }) as QuizSection | undefined;
    return exact ?? nearestSectionByStart(questionIndex, SECTION_QUESTION_COUNT);
  };

  const getQuestionHeading = (questionName: string): QuizHeading | undefined => {
    const questionIndex = findPageIndex(questionName);
    const exact = _.findKey(HEADING_CONFIG, (_value, section) => {
      const { startIndex, endIndex } = SECTION_QUESTION_COUNT_HEADINGS[section as QuizHeading];
      return questionIndex >= startIndex && questionIndex <= endIndex;
    }) as QuizHeading | undefined;
    return exact ?? nearestSectionByStart(questionIndex, SECTION_QUESTION_COUNT_HEADINGS);
  };

  return { findPageIndex, getQuestionSection, getQuestionHeading, getSectionInfo };
};

// Default-bound nav: identical behavior to the previous module-level consts.
// (GETTING_STARTED/FOUNDATIONS startIndex=0 is a no-op here since goals is index 0.)
const defaultNav = makeQuizNav(SURVEY_JSON.pages);
const findPageIndex = defaultNav.findPageIndex;
const getQuestionSection = defaultNav.getQuestionSection;
const getQuestionHeading = defaultNav.getQuestionHeading;
const getSectionInfo = defaultNav.getSectionInfo;

export {
  makeQuizNav,
  isValueChoice,
  findPageIndex,
  getQuestionSection,
  getQuestionHeading,
  getSectionInfo,
  COMPONENT_CONFIG,
  isTransitionComponentKey,
  QuizSection,
  SECTION_CONFIG,
  SURVEY_JSON,
};

export type {
  SectionConfig,
  SectionTotals,
  QuizNav,
}
